import { Component, OnInit, inject } from '@angular/core';
import { MatTreeNestedDataSource, MatTree, MatTreeNodeDef, MatNestedTreeNode, MatTreeNodePadding, MatTreeNodeToggle, MatTreeNodeOutlet, MatTreeNode } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { FaqsService } from 'src/app/services/faqs.service';
import { FaqModel } from 'src/app/models/faq.model';
import { faqTypes } from 'src/assets/data/faq_type_config';
import { FormControl, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import { ServiceModel } from 'src/app/models/service.model';
import { ProductService } from 'src/app/services/product.service';
import { Observable } from 'rxjs/internal/Observable';
import { startWith } from 'rxjs/internal/operators/startWith';
import { map } from 'rxjs/internal/operators/map';
import { ActivatedRoute } from '@angular/router';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';

import { MatButton } from '@angular/material/button';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';
import { EmptyStateComponent } from 'src/app/shared/ui/empty-state/empty-state.component';

@Component({
    selector: 'faqs',
    templateUrl: './faqs.component.html',
    styleUrls: ['./faqs.component.scss'],
    imports: [MatTree, MatTreeNodeDef, MatNestedTreeNode, MatTreeNodePadding, MatTreeNodeToggle, MatIconButton, MatIcon, MatTreeNodeOutlet, MatTreeNode, MatFormField, MatSelect, FormsModule, ReactiveFormsModule, MatOption, MatInput, CdkTextareaAutosize, MatAutocompleteTrigger, MatAutocomplete, AsyncPipe, MatButton, PageHeaderComponent, EmptyStateComponent]
})

export class FAQsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly faqSrv = inject(FaqsService);
  private readonly toastr = inject(ToastService);
  private readonly productSvc = inject(ProductService);



  treeControl = new NestedTreeControl<FaqModel>(node => node.children);
  dataSource = new MatTreeNestedDataSource<FaqModel>();
  /** True after the first tree response; avoids showing the empty state while loading. */
  treeLoaded = false;

  /** Same component serves client and contractor FAQs; the route says which. */
  get pageTitle(): string {
    return this.route.snapshot.data['serviceType'] === 'ContractorFaqs'
      ? 'Preguntas frecuentes de prestadores'
      : 'Preguntas frecuentes de clientes';
  }
  selectedNode?: FaqModel | null;
  selectedFaqType = new FormControl(0, [Validators.required, Validators.min(1)]);
  selectedDescription = new FormControl("", [Validators.required, Validators.minLength(1)]);
  // Always required: isFormValid only checks them for their own type (4 = Pregunta, 5 = Servicio). A validator chosen
  // from the type at construction time never applied, so a Servicio saved without its channel.
  selectedQuestion = new FormControl("", [Validators.required, Validators.minLength(1)]);
  selectedCustomerService: UntypedFormControl = new FormControl(null, Validators.required);
  faqTypes = faqTypes;
  isNewNode: boolean = true;
  services: ServiceModel[] = [];
  filteredOptions!: Observable<ServiceModel[]>;
  expandedNodeIds: Set<string> = new Set<string>();

  ngOnInit() {

    //Contractors or Client service FAQs according to parameter 'serviceType' from routing
    this.faqSrv.setBackUrl(this.route.snapshot.data['serviceType']);
    this._getTreeData();

    this.productSvc.getServices().subscribe(data => {
      this.services = data;
    });

    this.filteredOptions = this.selectedCustomerService.valueChanges
      .pipe(
        startWith(''),
        // The control holds the picked ServiceModel, not text: filter by its name.
        map(value => this._filter(typeof value === 'string' ? value : value?.name ?? ""))
      );
  }

  _getTreeData() {
    this.faqSrv.getTree(false).subscribe(data => {
      this.dataSource.data = data;
      this.treeControl.dataNodes = data;
      this.treeLoaded = true;
      this.expandNodesById(this.treeControl.dataNodes);
    });
  }

  private _filter(value: string): ServiceModel[] {
    const filterValue = value?.toLowerCase();
    if(filterValue)
      return this.services.filter(option => option.name.toLowerCase().includes(filterValue));

    return this.services;
  }

  optionClicked(event: Event, option: ServiceModel) {
    this.selectedCustomerService.patchValue(option);
  }
  
  hasChild = (_: number, node: FaqModel) => !!node.children && node.children.length > 0;

  editNode(node: FaqModel): void {
    this.isNewNode = false;
    this.selectedNode = node;
    this.selectedFaqType.setValue(node.fieldType);
    this.selectedDescription.setValue(node.description);
    this.selectedQuestion.setValue(node.question);
    this.selectedCustomerService.setValue(
      node.customerServiceId ? this.services.find(x => x.id.toString() == node.customerServiceId) ?? null : null);
  }

  deleteNode(node: FaqModel): void {
    this.faqSrv.delete(node.id!).subscribe(data => {
      this.toastr.success('Borrado', 'Se eliminó la opción correctamente.');
      this._getTreeData();
    }, (error) => this.toastr.error('Error', 'Se produjo un error al eliminar. Reintente'));
  }

  addNode(currentNode: FaqModel): void {
    this.cleanForm();
    this.selectedNode = currentNode;
    this.isNewNode = true;
  }

  showHide(node: FaqModel, action: boolean) {
    this.selectedNode = node;
    this.faqSrv.active(node.id!, action).subscribe(data => {
      this.selectedNode!.isActive = action;
      this.toastr.success('Actualización', 'Se actualizaron los datos correctamente.');
    }, (error) => this.toastr.error('Error', 'Se produjo un error al actualizar los datos.'));
  }

  /** reset() (not setValue) so the emptied required fields are untouched and don't turn red after Cancelar/Guardar. */
  cleanForm(): void {
    this.isNewNode = true;
    this.selectedNode = null;
    this.selectedFaqType.reset(0);
    this.selectedDescription.reset("");
    this.selectedQuestion.reset("");
    this.selectedCustomerService.reset(null);
  }

  saveNode() {
    if (this.isFormValid) {
      let newFaq: FaqModel = {
        id: this.isNewNode ? null : this.selectedNode?.id,
        parentId: this.isNewNode ? this.selectedNode?.id : this.selectedNode?.parentId,
        fieldType: this.selectedFaqType.value!,
        description: this.selectedDescription.value!,
        question: this.selectedQuestion.value!,
        customerServiceId: this.selectedCustomerService.value?.id.toString() ?? null,
        isActive: true,
        children: []
      };

      if(!this.isNewNode) {
        this.faqSrv.update(newFaq).subscribe(data => {
          this.cleanForm();
          this._getTreeData();
          this.toastr.success('Actualización', 'Se actualizaron los datos correctamente.');
        }, (error) => this.toastr.error('Error', 'Se produjo un error al actualizar los datos.'));
      }
      else {
        this.faqSrv.post(newFaq).subscribe(data => {
          newFaq.id = data;
          this.cleanForm();
          this._getTreeData();
          this.toastr.success('Guardado', 'Se guardaron los datos correctamente.');
        }, (error) => this.toastr.error('Error', 'Se produjo un error al guardar los datos.'));
      }
    }
  }

  get isFormValid(): boolean { 
    return  this.selectedFaqType.valid && 
            this.selectedDescription.valid && 
            (this.selectedFaqType.value == 4 ? this.selectedQuestion.valid : true) &&
            (this.selectedFaqType.value == 5 ? this.selectedCustomerService.valid : true); 
  }

  expandNode(nodeId: string) {
    if(this.expandedNodeIds.has(nodeId))
      this.expandedNodeIds.delete(nodeId);
    else
      this.expandedNodeIds.add(nodeId);
  }

  expandNodesById(treeNodes: any[]): void {
    treeNodes.forEach(node => {
      if (this.expandedNodeIds.has(node.id)) {
        this.treeControl.expand(node);

        // If the node has children, expand them if neccessary
        if (node.children?.length > 0) {
          this.expandNodesById(node.children);
        }
      } else if (node.children?.length > 0) {
        // Continue searching in child nodes
        this.expandNodesById(node.children);
      }
    });
  }
}